// ABOUTME: Orchestration hook for the Overbooking Allowance entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useOverbookingAllowanceConfig } from './useOverbookingAllowanceConfig'
import { useCreateOverbookingAllowance, useCreateOverbookingAllowanceWithScope, useUpdateOverbookingAllowance, useDeleteOverbookingAllowance } from './useOverbookingAllowanceMutations'
import { OverbookingAllowanceConfig, OverbookingAllowanceFormValues, OverbookingAllowanceEntryFormValues } from '../types/overbookingAllowance'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

/** Strip _rowId (UI-only) before equality comparison */
const stripRowId = (entries: OverbookingAllowanceEntryFormValues[]) =>
  entries.map(({ _rowId: _, ...rest }) => rest)

const equalsParent = (values: OverbookingAllowanceFormValues, config: OverbookingAllowanceConfig): boolean =>
  JSON.stringify(stripRowId(values.entries)) === JSON.stringify(stripRowId(config.parentValues.entries))

interface UseOverbookingAllowanceEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: OverbookingAllowanceFormValues) => void
}

interface UseOverbookingAllowanceEntityResult {
  config: OverbookingAllowanceConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

export const useOverbookingAllowanceEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseOverbookingAllowanceEntityParams): UseOverbookingAllowanceEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useOverbookingAllowanceConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({ entries: cfg.entries })), [rawConfig, pendingScope])

  const createMutation = useCreateOverbookingAllowance(countryId, businessLine)
  const createWithScopeMutation = useCreateOverbookingAllowanceWithScope(countryId, businessLine)
  const updateMutation = useUpdateOverbookingAllowance(countryId, businessLine)
  const deleteMutation = useDeleteOverbookingAllowance(countryId, businessLine)

  const formValuesRef = useRef<OverbookingAllowanceFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<OverbookingAllowanceConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: OverbookingAllowanceFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
    if (!formValuesRef.current) return
    const values = formValuesRef.current
    const currentConfig = configRef.current
    const scopeToCreate = pendingScopeRef.current
    if (scopeToCreate) {
      if (values.entries.length === 0 || (currentConfig && equalsParent(values, currentConfig))) {
        onSuccess({})
        return
      }
      mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
      return
    }
    if (!scopeId) return
    if (values.entries.length === 0 || (scopeId !== rootScopeId && currentConfig && equalsParent(values, currentConfig))) {
      if (configIdRef.current) {
        mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
      } else {
        onSuccess({ wasDelete: true })
      }
      return
    }
    const isNew = !configIdRef.current
    const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
    mutate({ scopeId, values }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
  }, [scopeId, rootScopeId])

  const handleReset = useCallback((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
    if (!scopeId) return
    if (!configIdRef.current) {
      onSuccess({ wasDelete: true })
      return
    }
    mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
  }, [scopeId])

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
